// Content styles.
// These can be used to display the edited HTML outside of the rich text editor.
// import 'froala-editor/css/froala_style.min.css';
// I18n
import 'froala-editor/js/languages/de.js';
// Lists
import 'froala-editor/js/plugins/lists.min.js';
// Content alignment
import 'froala-editor/js/plugins/align.min.js';
// Text and background colors
import 'froala-editor/js/plugins/colors.min.js';
// Images
import 'froala-editor/js/plugins/image.min.js';
// Links
import 'froala-editor/js/plugins/link.min.js';
// Tables
import 'froala-editor/js/plugins/table.min.js';
// Font Size
import 'froala-editor/js/plugins/font_size.min.js';
// Font Family
import 'froala-editor/js/plugins/font_family.min.js';
// Font Emoticons
import 'froala-editor/js/plugins/emoticons.min.js';
// Font Special Characters
import 'froala-editor/js/plugins/special_characters.min.js';
// URL
import 'froala-editor/js/plugins/url.min.js';
// Word Paste Plugin
// https://froala.com/word-paste-plugin/
import 'froala-editor/js/plugins/word_paste.min.js';
// Code view
import 'froala-editor/js/plugins/code_view.min.js';
// Code Beautifier
import 'froala-editor/js/plugins/code_beautifier.min.js';
// Custom plugins
// Mentions
import './plugins/mention/mention';
import './plugins/textmark/textmark';

import '@work4all/assets/text-editor/mention.css';
import './TextEditor.module.scss';
// Froala
// Core
import 'froala-editor/css/froala_editor.pkgd.min.css';

import { Popover, PopoverProps, Theme, useMediaQuery } from '@mui/material';
import clsx from 'clsx';
import Froalaeditor from 'froala-editor';
import i18next from 'i18next';
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import FroalaEditor from 'react-froala-wysiwyg';
import { useTranslation } from 'react-i18next';
import sanitizeHtml from 'sanitize-html';

import { usePageZoom, useTokenRefresherContext } from '@work4all/data';
import { decodeJWT, isTokenValid } from '@work4all/data/lib/utils/token-utils';

import { User } from '@work4all/models/lib/Classes/User.entity';

import { reactRefSetter } from '@work4all/utils';
import { useDeepMemo } from '@work4all/utils/lib/hooks/use-deep-memo';
import { useLatest } from '@work4all/utils/lib/hooks/use-latest';

import { ListEntityPickerInterface } from '../../../components/entity-picker/components/list-entity-picker/ListEntityPicker';
import { TextBuildingBlockPicker } from '../../../components/entity-picker/TextBuildingBlockPicker';
import { TextmarkPicker } from '../../../components/entity-picker/textmark-picker';
import { ToniAIMini } from '../../../toni-ai/components/toni-ai-mini/ToniAIMini';
import { useToniAIContext } from '../../../toni-ai/components/ToniAIChatBotProvider';
import {
  getTokenFromFileUploadUrl,
  updateTokenFileUploadUrl,
} from '../../../utils/generateFileUploadUrl';
import { useEditorToolbarContext } from '../use-editor-toolbar';

import {
  ALL_FONTS,
  DEFAULT_FONT,
  DEFAULT_RTF_FONT,
  RTF_FONTS,
  TEXT_EDITOR_DEFAULT_CONFIG,
} from './config';
import useComposeToniAiEmail from './hooks/useComposeToniAiEmail';
import {
  MentionPickerInline,
  MentionPickerMenu,
} from './plugins/mention/MentionPicker';
import { useMentionOptions } from './plugins/mention/use-mention-options';
import { createMentionElement } from './plugins/mention/utils';
import { GetEditorResult, ITextEditorProps } from './types';
import { useAutoCursorPlacement } from './use-auto-cursor-placement';
import {
  INERT_LINK_ICON,
  MORE_ICON_PATH,
  TEXT_ALIGN_ICON_PATH,
  TEXT_BUILDING_BLOCK_ICON_PATH,
  TEXT_COLOR_ICON_PATH,
  TEXT_HIGHLIGHT_ICON_PATH,
  TEXTMARK_ICON_PATH,
  TONI_AI_ICON_PATH,
} from './use-froala-icons';
import { setupSignatureProtection } from './utils/signature-protection';

Froalaeditor.DefineIconTemplate(
  'toniAiButton',
  '<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">[PATH]</svg>'
);

Froalaeditor.DefineIcon('backgroundColor', {
  NAME: 'backgroundColor',
  PATH: TEXT_HIGHLIGHT_ICON_PATH,
});

Froalaeditor.DefineIcon('customLinkEdit', {
  NAME: 'insertLinkIcon',
  PATH: INERT_LINK_ICON,
});

Froalaeditor.DefineIcon('moreText', {
  NAME: 'moreText',
  PATH: MORE_ICON_PATH,
});

Froalaeditor.DefineIcon('moreRich', {
  NAME: 'moreRich',
  PATH: TEXT_ALIGN_ICON_PATH,
});

Froalaeditor.DefineIcon('moreParagraph', {
  NAME: 'moreParagraph',
  PATH: MORE_ICON_PATH,
});

Froalaeditor.DefineIcon('textColor', {
  NAME: 'textColor',
  PATH: TEXT_COLOR_ICON_PATH,
});

Froalaeditor.DefineIcon('textmark', {
  NAME: 'textmark',
  PATH: TEXTMARK_ICON_PATH,
});

Froalaeditor.DefineIcon('textbuildingblock', {
  NAME: 'textbuildingblock',
  PATH: TEXT_BUILDING_BLOCK_ICON_PATH,
});

Froalaeditor.DefineIcon('toniAI', {
  NAME: 'toniAI',
  PATH: TONI_AI_ICON_PATH,
});

export const TextEditor = forwardRef<FroalaEditor, ITextEditorProps>(
  (props: ITextEditorProps, ref) => {
    const {
      defaultValue,
      value: valueProp,
      onChange,
      onBlur,
      onInitialized,
      config,
      enableMentions = false,
      enableTextmarks = false,
      enableTextBuildingBlocks = false,
      rtf = false,
      disableAddons = false,
      hideToolbar: hideToolbarProp = false,
      disabled = false,
      applyZoomLevel = true,
      zIndex = 9999,
      heightMax,
      placeholderText,
      textmarkType,
      autoCursorPositionSelector,
      showToniAI = false,
      sanitizeToUrlsOnly = false,
      isWork4allUser = false,
      aiToolsUsageEnabled = false,
      setAiToolsUsageEnabled = () => {},
      aiToolsAvailable = false,
    } = props;

    const tokenRefresherContext = useTokenRefresherContext();

    const canAddOrInsertImage = useMemo(() => {
      if (props.canAddOrInsertImage !== undefined)
        return props.canAddOrInsertImage;
      return !!config?.imageUploadURL;
    }, [config?.imageUploadURL, props.canAddOrInsertImage]);

    const ignoreButtons = useDeepMemo(() => {
      if (!canAddOrInsertImage) {
        if (Array.isArray(props.ignoreButtons))
          return [...props.ignoreButtons, 'insertImage'];
        return ['insertImage'];
      }

      return props.ignoreButtons;
    }, [props.ignoreButtons]);

    const [value, setValue] = useState(valueProp ?? defaultValue ?? '');

    useEffect(() => {
      if (valueProp !== undefined) {
        setValue(valueProp);
      }
    }, [valueProp]);

    const { t } = useTranslation();

    const { hideToolbar: hideToolbarContext } = useEditorToolbarContext();

    const hideToolbar = hideToolbarProp || hideToolbarContext;

    const { zoomLevel } = usePageZoom();
    const theme = document.querySelector('html').getAttribute('data-theme');
    const customTextColor = theme === 'dark' ? 'white' : 'black';
    const customZoomLevel = applyZoomLevel ? zoomLevel / 100 : 1;

    // <FroalaEditor> sometimes calls `onModelChange` after rendering if the
    // value is not formatted correctly. This component does not behave like
    // most controlled components and, if you pass it a `value` prop, it can
    // change its internal state to a different value even if the value of
    // `value` prop does not change.
    //
    // We can use the following workaround to avoid emitting `onChange` if it
    // happens as a result of "fixing" the `value` prop and not because of a
    // user interaction. The <FroalaEditor>  component will still display the
    // content correctly after reformatting it, but the parent component's state
    // will not be updated. This will resolve forms becoming `dirty` without any
    // user interaction.

    const [editor, setEditor] = useState<FroalaEditor>();
    const latestEditorRef = useLatest(editor);

    const wrapQueryRef = useRef<HTMLDivElement>(null);

    // Keep track of the current value and its "fixed" version after Froala
    // cleans it up.
    const processedValueCleanedRef = useRef<{ value: string; cleaned: string }>(
      {
        value: undefined,
        cleaned: undefined,
      }
    );

    const editorRef = useRef(null);
    const cursorRef = useRef(false);

    useAutoCursorPlacement({
      editorRef,
      cursorRef,
      autoCursorPositionSelector,
      value,
      editor,
    });

    useEffect(() => {
      // The editor might not be initialized yet when this is called.
      // In this case just store `cleaned` value as `undefined`.
      const cleaned = editor?.getEditor()?.html?.get();

      processedValueCleanedRef.current = {
        value: value,
        cleaned: cleaned,
      };
    }, [value, editor]);

    const [toniAiHtml, setToniAiHtml] = useState('');

    const handleModelChange = (model: string) => {
      setValue(model);

      // Froala will sometimes call `onModelChange` during rendering. Waiting
      // for effects on `value` prop let's us avoid that.

      // Only call `onChange` if the value has been processed before (if the
      // editor rendered and cleaned the value) and the new value is different
      // from just "cleaned" current value. User caused updates will always happen
      // after rendering, so this should never skip those.

      const processed = processedValueCleanedRef.current;

      const isValueProcessed =
        processed.value === value && processed.cleaned !== undefined;
      const isValueChanged = model !== processed.cleaned;

      if (isValueProcessed && isValueChanged) {
        onChange?.(model);
      }
    };

    const [mentionState, setMentionState] = useState<{
      active: boolean;
      text: string;
      anchorEl: Element;
    } | null>({ active: false, text: '', anchorEl: null });

    const mentionOptions = useMentionOptions({
      text: mentionState.text,
    });

    const mentionPopoverState = useMemo<{
      open: boolean;
      users: User[];
      anchorEl: Element;
    }>(() => {
      return {
        open: mentionState.active && mentionOptions.length > 0,
        users: mentionOptions,
        anchorEl: mentionState.anchorEl,
      };
    }, [mentionState, mentionOptions]);

    const [mentionMenuPopoverState, setMentionMenuPopoverState] = useState<{
      open: boolean;
      anchorEl: Element;
    }>({ open: false, anchorEl: null });

    const isMobile = useMediaQuery<Theme>((theme) =>
      theme.breakpoints.down('md')
    );

    useEffect(() => {
      editor?.getEditor()?.mention?.setActive(mentionPopoverState.open);
    }, [editor, mentionPopoverState.open]);

    const { setEMailRequestData } = useToniAIContext();

    const [toniAiActive, setToniAiActive] = useState(false);
    const htmlBeforeSelectionRef = useRef<string>('');

    const finalConfig = useMemo(() => {
      const moreMiscTextButtonSection = rtf
        ? enableTextBuildingBlocks
          ? ['|', 'textbuildingblock']
          : []
        : [
            '|',
            ...(enableTextBuildingBlocks ? ['textbuildingblock'] : []),
            ...(enableTextmarks ? ['textmark'] : []),
            ...(enableMentions ? ['insertMention'] : []),
            ...(disableAddons ? [] : moreMiscButtons2),
          ];

      const finalToolbarConf = (toolbarConf) => {
        const result = {
          ...toolbarConf,
          moreMisc: {
            ...toolbarConf.moreMisc,
            buttons: [
              ...toolbarConf.moreMisc.buttons,
              ...moreMiscTextButtonSection,
            ],
          },
          moreText: {
            ...toolbarConf.moreText,
            buttons: [
              ...(showToniAI && aiToolsAvailable ? ['toniAI', '|'] : []),
              ...toolbarConf.moreText.buttons,
            ],
          },
        };
        return result;
      };

      const finalConfigBeforeIgnoringButtons = {
        heightMax,
        ...TEXT_EDITOR_DEFAULT_CONFIG,
        toolbarSticky: isMobile
          ? false
          : TEXT_EDITOR_DEFAULT_CONFIG.toolbarSticky,
        autofocus: true,
        placeholderText: placeholderText ?? t('COMMON.ENTER_TEXT'),
        fontFamilySelection: true,
        listAdvancedTypes: true,
        toolbarButtons: finalToolbarConf(
          TEXT_EDITOR_DEFAULT_CONFIG.toolbarButtons
        ),
        toolbarButtonsXS: finalToolbarConf(
          TEXT_EDITOR_DEFAULT_CONFIG.toolbarButtonsXS
        ),
        toolbarButtonsSM: finalToolbarConf(
          TEXT_EDITOR_DEFAULT_CONFIG.toolbarButtonsSM
        ),
        toolbarButtonsMD: finalToolbarConf(
          TEXT_EDITOR_DEFAULT_CONFIG.toolbarButtonsMD
        ),
        imagePaste: canAddOrInsertImage,
        imageUpload: canAddOrInsertImage,
        ...config,
        enableMentions,
        customBlockedElements: props.blockedElements,
        language: i18next.language,
        zIndex,
        wordPasteModal: false,
        fontFamilyDefaultSelection: rtf ? DEFAULT_RTF_FONT : DEFAULT_FONT,
        fontSizeDefaultSelection: rtf ? 10 : 12,
        fontFamily: rtf ? RTF_FONTS : ALL_FONTS,
        events: {
          ...(config?.events as Record<string, unknown>),
          'popups.show.image.edit': function () {
            const popup = this.popups.get('image.edit');
            const image = this.image.get();

            if (popup && image) {
              const currentTop = popup.css('top');
              const parsedCurrentTop = parseInt(currentTop);
              const imageHeight = image.height();
              const captionHeight =
                image.next('.fr-image-caption')?.outerHeight?.() ?? 0;
              const popupHeight = popup.height();

              if (imageHeight + captionHeight < popupHeight + 24) {
                popup.css(
                  'top',
                  `${parsedCurrentTop + imageHeight + captionHeight + 12}px`
                );
              }
            }
          },
          'image.error': async (event) => {
            // verify if token was expired before
            const self = editorRef.current.editor;
            const decodedToken = decodeJWT(
              getTokenFromFileUploadUrl(self.opts.imageUploadURL)
            );
            if (
              event.code === 3 &&
              tokenRefresherContext?.refreshToken &&
              !isTokenValid(decodedToken.payload).valid
            ) {
              const token = await tokenRefresherContext?.refreshToken?.();
              const imageUploadURL = updateTokenFileUploadUrl(
                self.opts.imageUploadURL,
                token
              );
              self.opts.imageUploadURL = imageUploadURL;
            }
          },
          click: function (e) {
            // Call the custom click handler if provided
            const customClickHandler = (
              config?.events as Record<string, unknown>
            )?.['click'];
            if (typeof customClickHandler === 'function') {
              const result = customClickHandler.call(this, e);
              if (result === false) return false;
            }

            // Simple click handler that only allows link clicks in signatures
            // Check if we clicked on a link inside signature
            const clickedLink =
              e.target.tagName === 'A' ? e.target : e.target.closest('a');
            if (clickedLink && clickedLink.closest('.signature')) {
              // Allow link clicks but prevent editor from showing popups
              setTimeout(() => {
                if (this.popups) this.popups.hideAll();
              }, 0);
              return true;
            }
          },
          mouseup: function (e) {
            // Simple mouseup handler to hide popups after selection in signatures
            const signature = e.target.closest('.signature');
            if (signature) {
              // Hide any popups that might appear
              setTimeout(() => {
                if (this.popups) this.popups.hideAll();
              }, 0);
            }
          },
          keydown: function (e) {
            // Call the custom keydown handler if provided
            const customKeydownHandler = (
              config?.events as Record<string, unknown>
            )?.['keydown'];
            if (typeof customKeydownHandler === 'function') {
              const result = customKeydownHandler.call(this, e);
              if (result === false) return false;
            }

            // Simple keydown handler to prevent any editing in signatures
            // The contenteditable="false" attribute handles most protection
            // This is just an extra safeguard
            try {
              const signatures = this.el.querySelectorAll('.signature');
              if (!signatures.length) return;

              const selection = this.selection.get();
              if (!selection || !selection.rangeCount) return;

              // Check if selection is inside a signature
              const range = selection.getRangeAt(0);
              for (const signature of signatures) {
                if (
                  signature.contains(range.startContainer) ||
                  signature.contains(range.endContainer)
                ) {
                  e.preventDefault();
                  e.stopPropagation();
                  return false;
                }
              }
            } catch (err) {
              console.warn('Error in keydown handler', err);
            }
          },
          initialized() {
            const editor = {
              getEditor: () => {
                return this;
              },
            } as FroalaEditor;

            setEditor(editor);

            // Schedule the event handlers to run after initialization is complete
            setTimeout(() => {
              setupSignatureProtection(this);
            }, 0);

            if (onInitialized) {
              onInitialized(latestEditorRef.current);
            }
          },
          'paste.beforeCleanup': (e: string) => {
            if (sanitizeToUrlsOnly) {
              return sanitizeHtml(e, {
                allowedTags: ['a', 'p', 'br'],
              });
            }
          },
          // Handle events from the custom mention plugin.
          'mention.input'({
            text,
            position,
          }: {
            text: string;
            position: DOMRectReadOnly;
          }) {
            const { width, height } = position;

            const x = position.x;
            const y = position.y;

            const virtualRect: DOMRect = DOMRect.fromRect({
              x,
              y,
              width,
              height,
            });
            interface PopoverVirtualElement {
              nodeType: 1;
              getBoundingClientRect: () => DOMRect;
            }

            const virtualEl: PopoverVirtualElement = {
              nodeType: 1,
              getBoundingClientRect: () => virtualRect,
            };

            const anchorEl = virtualEl as Element;

            setMentionState({ active: true, text, anchorEl });
          },
          'mention.keydown'(key: string) {
            // Froala uses fake events that don't work with ListEntityPicker.
            // Remap the key and create a custom event with compatible
            // interface.

            const keyToCode: Record<string, string> = {
              ArrowUp: 'ArrowUp',
              ArrowDown: 'ArrowDown',
              Enter: 'Enter',
              Tab: 'Enter', // Also select the active option on Tab key.
            };

            const code = keyToCode[key];

            if (code) {
              //TODO change to inlineMentionPopoverPickerRef.current.goTo/select once mentioning picker is refactored
              const event = new KeyboardEvent('keydown', { code });
              inlineMentionPopoverPickerRef.current?.handleKeyboardEvent(event);
            }
          },
          'mention.reset'() {
            setMentionState((state) => ({ ...state, active: false }));
          },
          'mention.buttons.insert'() {
            const buttonEl = document.querySelector(
              "[data-cmd='insertMention']"
            );

            setMentionMenuPopoverState({ open: true, anchorEl: buttonEl });
          },
          'textbuildingblock.focus'(context) {
            setPopoverAnchorEl(
              document.getElementById(`textbuildingblock-${context.id}`)
            );
            setTextBuildingBlockContext(context);
          },
          'textmark.focus'(context) {
            setPopoverAnchorEl(
              document.getElementById(`textmark-${context.id}`)
            );
            setTextmarkPickerOpen(true);
          },
          'toniAI.focus': function (context) {
            const wrapperEl = wrapQueryRef.current.querySelector('.fr-view');
            const html = wrapperEl.innerHTML;

            setToniAiHtml(html);

            setEMailRequestData({
              email_body: html,
              email_title: '',
            });

            setPopoverAnchorEl(document.getElementById(`toniAI-${context.id}`));
            setToniAIPopoverOpen(true);

            htmlBeforeSelectionRef.current =
              editorRef.current?.editor?.html?.get();

            editorRef.current?.getEditor()?.colors?.background('#C3DDFC');
          },
          'html.beforeGet': function () {
            /**
             * wrap text in default styles
             * this is needed because otherwise the text doesn't have styling if not manually set
             */
            if (rtf) {
              const wrappingDiv = this.el.querySelector('div');
              if (wrappingDiv) {
                wrappingDiv.style.fontFamily = 'Arial, sans-serif';
                wrappingDiv.style.fontSize = '10pt';
              }
            }
          },
          blur: function () {
            onBlur?.(this.html.get());
          },
        },
      };

      let finalConfig = finalConfigBeforeIgnoringButtons;
      if (ignoreButtons) {
        finalConfig = {
          ...finalConfig,
          toolbarButtons: { ...finalConfig.toolbarButtons },
          toolbarButtonsXS: {
            ...finalConfig.toolbarButtonsXS,
            moreRich: {
              ...finalConfig.toolbarButtonsXS.moreRich,
              buttons: [finalConfig.toolbarButtonsXS.moreRich.button],
            },
          },
          toolbarButtonsSM: {
            ...finalConfig.toolbarButtonsSM,
            moreRich: {
              ...finalConfig.toolbarButtonsSM.moreRich,
              buttons: [finalConfig.toolbarButtonsSM.moreRich.button],
            },
          },
          toolbarButtonsMD: {
            ...finalConfig.toolbarButtonsMD,
            moreRich: {
              ...finalConfig.toolbarButtonsMD.moreRich,
              buttons: [finalConfig.toolbarButtonsMD.moreRich.button],
            },
          },
        };

        ignoreButtons.forEach((button) => {
          const toolbarXSMoreRichButtons = (finalConfig?.toolbarButtonsXS
            ?.moreRich?.buttons ?? []) as string[];
          const toolbarSMMoreRichButtons = (finalConfig?.toolbarButtonsSM
            ?.moreRich?.buttons ?? []) as string[];
          const toolbarMDMoreRichButtons = (finalConfig?.toolbarButtonsMD
            ?.moreRich?.buttons ?? []) as string[];
          const toolbarButtons = (finalConfig?.toolbarButtons ??
            []) as typeof TEXT_EDITOR_DEFAULT_CONFIG.toolbarButtons;

          if (button === 'insertImage') {
            if (toolbarXSMoreRichButtons.includes(button)) {
              finalConfig.toolbarButtonsXS.moreRich.buttons =
                toolbarXSMoreRichButtons.filter((value) => value !== button);
            }

            if (toolbarSMMoreRichButtons.includes(button)) {
              finalConfig.toolbarButtonsSM.moreRich.buttons =
                toolbarSMMoreRichButtons.filter((value) => value !== button);
            }

            if (toolbarMDMoreRichButtons.includes(button)) {
              finalConfig.toolbarButtonsMD.moreRich.buttons =
                toolbarMDMoreRichButtons.filter((value) => value !== button);
            }
            const entries = Object.entries(toolbarButtons);
            const last = entries[entries.length - 1];

            if (toolbarButtons[last[0]].buttons.includes(button)) {
              finalConfig.toolbarButtons[last[0]].buttons =
                finalConfig.toolbarButtons[last[0]].buttons.filter(
                  (value) => value !== button
                );
            }
          }
        });
      }

      return finalConfig;
    }, [
      rtf,
      enableTextBuildingBlocks,
      enableTextmarks,
      enableMentions,
      disableAddons,
      heightMax,
      isMobile,
      placeholderText,
      t,
      canAddOrInsertImage,
      config,
      props.blockedElements,
      zIndex,
      ignoreButtons,
      showToniAI,
      aiToolsAvailable,
      tokenRefresherContext,
      onInitialized,
      latestEditorRef,
      sanitizeToUrlsOnly,
      setEMailRequestData,
      onBlur,
    ]);

    const [textmarkPickerOpen, setTextmarkPickerOpen] = useState(false);
    const [toniAIPopoverOpen, setToniAIPopoverOpen] = useState(false);
    const [textBuildingBlockContext, setTextBuildingBlockContext] =
      useState<GetEditorResult | null>(null);
    const [popoverAnchorEl, setPopoverAnchorEl] = useState<Element>(null);

    useEffect(() => {
      Froalaeditor.RegisterCommand('textmark', {
        title: t('COMMON.TEXTMARK'),
        icon: 'textmark',
        focus: true,
        undo: true,
        refreshAfterCallback: false,
        callback: function () {
          this.selection.save();
          (this.opts?.events as unknown)?.['textmark.focus']?.(this);
        },
      });

      Froalaeditor.RegisterCommand('textbuildingblock', {
        title: t('COMMON.TEXTBUILDINGBLOCK'),
        icon: 'textbuildingblock',
        focus: true,
        undo: true,
        refreshAfterCallback: false,
        callback: function () {
          this.selection.save();
          (this.opts?.events as unknown)?.['textbuildingblock.focus']?.(this);
        },
      });

      Froalaeditor.RegisterCommand('toniAI', {
        title: t('COMMON.TONI_AI_TOOLTIP'),
        icon: 'toniAI',
        focus: true,
        undo: true,
        refreshAfterCallback: false,
        callback: function () {
          this.selection.save();
          (this.opts?.events as unknown)?.['toniAI.focus']?.(this);
        },
      });

      Froalaeditor.RegisterCommand('moreRich', {
        title: t('COMMON.MORERICH_TITLE'),
        icon: 'moreRich',
      });

      // Register insert command with linkEdit functionality
      Froalaeditor.RegisterCommand('customLinkEdit', {
        title: t('COMMON.ADD_EDIT_LINK'),
        focus: true,
        undo: true,
        refreshAfterCallback: true,
        callback: function () {
          const selection = this.selection.get();
          const hasSelection =
            selection && selection.toString().trim().length > 0;

          // If there's selected text, use it for the link
          if (hasSelection) {
            // Show link insertion popup
            this.popups.show('link.insert');
            const popup = this.popups.get('link.insert');
            if (popup) {
              // Find link input field and focus it
              const urlField = popup.find('.fr-link-attr[name="href"]');
              if (urlField) {
                urlField.focus();
              }
            }
          } else {
            // No selection, use the standard insert link command
            this.commands.exec('insertLink');
          }
        },
      });
    }, [t]);

    const TEXT_BLOCK_SIZE = 400;
    const isTopAnchor =
      window.innerHeight - popoverAnchorEl?.getBoundingClientRect().top >
      TEXT_BLOCK_SIZE;

    const anchorOrigin: PopoverProps['anchorOrigin'] = isTopAnchor
      ? {
          horizontal: 'right',
          vertical: 'bottom',
        }
      : {
          horizontal: 'right',
          vertical: 'top',
        };

    const transformOrigin: PopoverProps['transformOrigin'] = isTopAnchor
      ? {
          horizontal: 'right',
          vertical: 'top',
        }
      : {
          horizontal: 'right',
          vertical: 'bottom',
        };

    const popoverProps: PopoverProps = {
      open: false,
      anchorReference: 'anchorEl',
      anchorEl: popoverAnchorEl,
      anchorOrigin,
      transformOrigin,
    };

    const editorInstance = useMemo(() => {
      return editor?.getEditor();
    }, [editor]);
    const isEditorInstanceReady = editorInstance?.ready;

    useEffect(() => {
      if (editorInstance) {
        if (disabled) {
          editorInstance.edit?.off();
        } else {
          editorInstance.edit?.on();
        }

        if (disabled || hideToolbar) {
          editorInstance?.toolbar?.hide();
        } else {
          editorInstance?.toolbar?.show();
        }
      }
    }, [editorInstance, isEditorInstanceReady, disabled, hideToolbar]);

    const inlineMentionPopoverPickerRef =
      useRef<ListEntityPickerInterface<User>>(null);

    const onCloseAutoMobileEvenCounter = useRef(0);

    const handleEditorRef = useCallback(
      (instance: FroalaEditor) => {
        if (instance && instance !== editor) {
          setEditor(instance);
        }
      },
      [editor]
    );

    const { composeEmail } = useComposeToniAiEmail();

    return (
      <div
        className={clsx('froala-wrap', {
          'enable-textmarks': enableTextmarks,
          rtf: rtf,
          dark: theme === 'dark',
        })}
        style={{
          '--custom-theme-color': customTextColor,
          '--custom-zoom': customZoomLevel,
        }}
        ref={wrapQueryRef}
      >
        <Popover
          {...popoverProps}
          open={toniAIPopoverOpen}
          onClose={() => {
            if (!toniAiActive) {
              setToniAIPopoverOpen(false);
              editorRef.current?.editor?.html?.set(
                htmlBeforeSelectionRef.current
              );
              editorRef.current?.editor?.selection.restore();
            }
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <ToniAIMini
            onSuccess={(result, mapping, completed) => {
              setToniAiActive(true);
              composeEmail(result, mapping, completed, editor, toniAiHtml);

              if (completed) {
                setToniAiActive(false);
                setToniAIPopoverOpen(false);
              }
            }}
            aiToolsUsageEnabled={aiToolsUsageEnabled}
            setAiToolsUsageEnabled={setAiToolsUsageEnabled}
          />
        </Popover>
        <Popover
          {...popoverProps}
          open={textmarkPickerOpen}
          onClose={() => setTextmarkPickerOpen(false)}
        >
          <TextmarkPicker
            editor={editorInstance}
            onChange={({ newEditorValue }) => {
              setTextmarkPickerOpen(false);
              handleModelChange(newEditorValue);
            }}
            type={textmarkType}
            isWork4allUser={isWork4allUser}
          />
        </Popover>
        <Popover
          {...popoverProps}
          disableAutoFocus
          open={Boolean(textBuildingBlockContext)}
          onClose={() => {
            // Somehow popover fires onClose in moment of open
            if (isMobile && onCloseAutoMobileEvenCounter.current === 0) {
              onCloseAutoMobileEvenCounter.current++;
              return;
            }
            onCloseAutoMobileEvenCounter.current = 0;
            setTextBuildingBlockContext(null);
          }}
        >
          <TextBuildingBlockPicker
            editor={textBuildingBlockContext}
            multiple={false}
            value={null}
            onChange={({ newEditorValue }) => {
              handleModelChange(newEditorValue);
              setTextBuildingBlockContext(null);
            }}
          />
        </Popover>

        <Popover
          disableAutoFocus
          anchorReference="anchorEl"
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          open={mentionPopoverState.open}
          anchorEl={mentionPopoverState.anchorEl}
          onClose={() => {
            setMentionState((state) => ({ ...state, active: false }));
            editor?.getEditor()?.events?.focus();
          }}
        >
          <MentionPickerInline
            autoFocus={false}
            pickerRef={inlineMentionPopoverPickerRef}
            users={mentionPopoverState.users}
            onSelect={(user) => {
              const element = createMentionElement({
                id: `${user.id}`,
                text: `@${user.displayName}`,
              });

              // On iOS/Safari with mouse click, the focus shifts to the picker
              // and selection state is lost. We need to restore it first.
              // The mention plugin stores the editState, but we need to ensure
              // it's properly available before replace() is called.
              editor?.getEditor()?.events?.focus();

              editor?.getEditor()?.mention?.replace(element);
              setMentionState((state) => ({ ...state, active: false }));
            }}
          />
        </Popover>

        <Popover
          disableAutoFocus
          anchorReference="anchorEl"
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          {...mentionMenuPopoverState}
          onClose={() => {
            setMentionMenuPopoverState({
              open: false,
              anchorEl: null,
            });
          }}
        >
          <MentionPickerMenu
            onSelect={async (user) => {
              const element = createMentionElement({
                id: `${user.id}`,
                text: `@${user.displayName}`,
              });

              await editor?.getEditor()?.mention?.insert(element);

              handleModelChange(editor?.getEditor()?.html.get());
              setMentionMenuPopoverState({
                open: false,
                anchorEl: null,
              });
            }}
          />
        </Popover>
        <FroalaEditor
          ref={reactRefSetter(ref, editorRef, handleEditorRef)}
          tag="textarea"
          config={finalConfig}
          model={value}
          onModelChange={handleModelChange}
        />
      </div>
    );
  }
);

const moreMiscButtons2 = [
  'insertLink',
  'insertImage',
  'insertTable',
  'embedly',
  'insertFile',
  'emoticons',
  'html',
];
